import { ExclamationCircleOutlined, PlusOutlined } from '@ant-design/icons';
import confirm from 'antd/lib/modal/confirm';
import moment from 'moment';
import { useEffect, useState } from 'react';

import { Button, Collapse, Drawer } from '../../../components/index';
import { useGetCargoTypeQuery } from '../../../services/cargoApi';
import {
  useCancelDockingMutation,
  useGetStopoverDockingsQuery,
} from '../../../services/dockingApi';
import { useGetOperatorsQuery } from '../../../services/stopoverApi';
import { useGetTugboatsQuery } from '../../../services/vesselApi';
import { Docking, Stopover as StopoverType } from '../../../types';
import { formatDockingToForm } from '../../../utils/formatters';
import { collapsePanelsListDocking } from '../../../utils/lists';
import { isStopoverInactive } from '../../../utils/validators';
import { AnchoringForm } from '../anchoringForm';
import { DockingDrawer } from '../DockingDrawer';
import { DockingStaymentDrawer } from '../dockingStaymentDrawer/DockingStaymentDrawer';
import { DockingDescription, DockingEventBlock } from './dockingDescription';

const { Panel } = Collapse;

/**
 * Os dois blocos da listagem.
 *
 * "Agora e próximas" traz os eventos não concluídos por inteiro e nasce
 * aberto, porque é onde está a manobra que o plantonista precisa achar.
 * "Histórico" traz os concluídos, nasce colapsado e pagina.
 */
const NOT_CONCLUDED_BLOCK: DockingEventBlock = {
  key: 'dockings-not-concluded',
  title: 'Agora e próximas',
  defaultOpen: true,
  emptyMessage: 'Nenhum evento em andamento ou programado nesta escala.',
};

const HISTORY_BLOCK: DockingEventBlock = {
  key: 'dockings-history',
  title: 'Histórico',
  defaultOpen: false,
  emptyMessage: 'Nenhum evento concluído nesta escala.',
  showEventEnd: true,
};

type DockingsDataProps = {
  selectedStopover?: StopoverType;
  setSelectedStopover: (value: StopoverType) => void;
  activeKey: string | string[];
  onOpenPanel: (panel: any) => void;
  allowedBerth: any[];
  screen: 'STOPOVER' | 'STAYMENT' | 'AGENT_AREA';
  // allowedOperators: any[];
  // onSelectBerth: (val: any, option: any) => void;
};

export function DockingsData(props: DockingsDataProps) {
  const {
    selectedStopover,
    setSelectedStopover,
    activeKey,
    onOpenPanel,
    allowedBerth,
    screen,
  } = props;
  const [selectedDocking, setSelectedDocking] = useState<Docking>(
    {} as Docking
  );
  const [selectedDockingPosition, setSelectedDockingPosition] = useState<
    number | null
  >(null);

  const [isDockingDrawerVisible, setIsDockingDrawerVisible] = useState(false);

  const [isVisibleAnchoringDrawer, setIsVisibleAnchoringDrawer] =
    useState(false);

  const [openCollapsePanels, setOpenCollapsePanels] = useState<
    string | string[]
  >(collapsePanelsListDocking);

  const [historyPage, setHistoryPage] = useState(1);
  const [historyPageSize, setHistoryPageSize] = useState<number>();
  const [historyTotal, setHistoryTotal] = useState<number>();

  const { data: cargosData } = useGetCargoTypeQuery();
  const { data: operatorsData } = useGetOperatorsQuery();
  const { data: tugboatsData } = useGetTugboatsQuery({});

  const [cancelDocking] = useCancelDockingMutation();

  // Um bloco por chamada: o cache do RTK Query separa as duas pelo argumento,
  // então cada bloco tem o seu ciclo de carregamento e o seu erro.
  const notConcluded = useGetStopoverDockingsQuery(
    { stopover: selectedStopover?.id, event_state: 'not_concluded' },
    { skip: !selectedStopover?.id }
  );
  const history = useGetStopoverDockingsQuery(
    {
      stopover: selectedStopover?.id,
      event_state: 'concluded',
      page: historyPage,
    },
    { skip: !selectedStopover?.id }
  );

  useEffect(() => {
    setHistoryPage(1);
    setHistoryPageSize(undefined);
    setHistoryTotal(undefined);
  }, [selectedStopover?.id]);

  useEffect(() => {
    if (history.currentData) {
      setHistoryTotal(history.currentData.count);
    }
  }, [history.currentData]);

  // O tamanho de página é do backend, não da tela. Uma página que tem `next`
  // está cheia, então o que ela devolveu é o tamanho de página; uma primeira
  // página sem `next` é o conjunto inteiro.
  useEffect(() => {
    const currentHistory = history.currentData;
    if (!currentHistory) {
      return;
    }
    if (currentHistory.next || historyPageSize === undefined) {
      setHistoryPageSize(Math.max(currentHistory.results.length, 1));
    }
  }, [history.currentData]);

  function onEdit(evt: any, dockingToEdit?: Docking) {
    evt.stopPropagation();
    if (!dockingToEdit) {
      return;
    }
    // Os formulários leem datas como Moment, que é a forma que
    // formatStopoverToForm dava às atracações vindas da escala.
    setSelectedDocking(formatDockingToForm(dockingToEdit));
    setSelectedDockingPosition(dockingToEdit.index || 0);
    if (
      dockingToEdit.purpose === 'ANCHORING_SERVICES' ||
      dockingToEdit.purpose === 'ANCHORING_WAIT'
    ) {
      setIsVisibleAnchoringDrawer(true);
    } else {
      setIsDockingDrawerVisible(true);
    }
  }

  function handleCancelDocking(evt: any, indexToCancel: number) {
    evt.stopPropagation();
    confirm({
      title: 'Deseja realmente cancelar esta atracação?',
      icon: <ExclamationCircleOutlined />,
      content:
        'Depois que salvar a escala a atracação cancelada não poderá ser reativada',
      onOk() {
        onCancelDocking(indexToCancel);
      },
      okText: 'Sim',
      cancelText: 'Não',
    });
  }

  function onCancelDocking(indexToCancel: number): void {
    if (selectedStopover && selectedStopover.dockings) {
      const canceledDocking = selectedStopover.dockings[indexToCancel];
      const allDockings: Docking[] = selectedStopover.dockings;

      const updatedDockings: Docking[] = allDockings.map((docking): Docking => {
        if (docking.index === canceledDocking.index) {
          return {
            ...docking,
            archived_at: moment().format('YYYY-MM-DD HH:mm'),
          };
        }
        return docking;
      });

      selectedStopover.dockings = updatedDockings;

      if (canceledDocking.id) {
        cancelDocking({ id: canceledDocking.id });
      }
    }
  }

  function onBack() {
    setIsDockingDrawerVisible(false);
  }

  function getPanelHeader() {
    if (screen === 'STAYMENT') {
      const total =
        (notConcluded.currentData?.count || 0) + (historyTotal || 0);
      return `Atracações e fundeios 0/${total}`;
    }
    return 'Atracações e fundeios previstos';
  }

  const blockAddDocking =
    selectedStopover?.current_status === 'DONE' ||
    isStopoverInactive(selectedStopover);

  return (
    <>
      <Collapse
        activeKey={activeKey}
        onChange={onOpenPanel}
        expandIconPosition="end"
      >
        <Panel forceRender header={getPanelHeader()} key="dockings">
          {!blockAddDocking && (
            <>
              <Button
                style={{ marginInlineEnd: '10px' }}
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setIsDockingDrawerVisible(true)}
              >
                Programar atracação
              </Button>
              <Button
                icon={<PlusOutlined />}
                onClick={() => setIsVisibleAnchoringDrawer(true)}
              >
                Programar fundeio
              </Button>
            </>
          )}
          <DockingDescription
            block={NOT_CONCLUDED_BLOCK}
            selectedDockings={notConcluded.currentData?.results}
            total={notConcluded.currentData?.count}
            isFetching={notConcluded.isFetching}
            isError={notConcluded.isError}
            isDockingDrawerVisible={isDockingDrawerVisible}
            onClickEdit={(evt, index) =>
              onEdit(evt, notConcluded.currentData?.results[index])
            }
            screen={screen}
            setOpenCollapsePanels={setOpenCollapsePanels}
          />
          <DockingDescription
            block={HISTORY_BLOCK}
            selectedDockings={history.currentData?.results}
            total={historyTotal}
            isFetching={history.isFetching}
            isError={history.isError}
            page={historyPage}
            pageSize={historyPageSize}
            onChangePage={setHistoryPage}
            isDockingDrawerVisible={isDockingDrawerVisible}
            onClickEdit={(evt, index) =>
              onEdit(evt, history.currentData?.results[index])
            }
            screen={screen}
            setOpenCollapsePanels={setOpenCollapsePanels}
          />
        </Panel>
      </Collapse>
      <DockingStaymentDrawer
        visible={isDockingDrawerVisible && screen === 'STAYMENT'}
        setIsVisible={setIsDockingDrawerVisible}
        selectedStopover={selectedStopover}
        setSelectedStopover={setSelectedStopover}
        currentDockingKey={selectedDockingPosition}
        setCurrentDockingKey={setSelectedDockingPosition}
        selectedDocking={selectedDocking}
        setSelectedDocking={setSelectedDocking}
        width={800}
        allowedBerth={allowedBerth}
        tugboatsData={tugboatsData?.results}
        cargosData={cargosData?.results}
        operatorsData={operatorsData?.results}
        onBack={() => onBack()}
      />
      <DockingDrawer
        visible={
          isDockingDrawerVisible &&
          (screen === 'STOPOVER' || screen === 'AGENT_AREA')
        }
        setIsVisible={setIsDockingDrawerVisible}
        selectedStopover={selectedStopover}
        setSelectedStopover={setSelectedStopover}
        selectedVessel={props.selectedStopover?.vessel}
        currentDockingKey={selectedDockingPosition}
        setCurrentDockingKey={setSelectedDockingPosition}
        selectedDocking={selectedDocking}
        setSelectedDocking={setSelectedDocking}
        width={900}
        allowedBerth={allowedBerth}
        onBack={() => onBack()}
        screen={screen === 'STAYMENT' ? 'STOPOVER' : screen}
        openCollapsePanels={openCollapsePanels}
        setOpenCollapsePanels={setOpenCollapsePanels}
      />
      <Drawer closable={false} visible={isVisibleAnchoringDrawer} width={820}>
        <AnchoringForm
          selectedStopover={selectedStopover}
          setSelectedStopover={setSelectedStopover}
          selectedDocking={selectedDocking}
          setSelectedDocking={setSelectedDocking}
          onClose={() => setIsVisibleAnchoringDrawer(false)}
          screen={screen}
        />
      </Drawer>
    </>
  );
}
